// Max upload size: 5 MB
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

// Utility: read a File/Blob into an HTMLImageElement
const blobToImage = (blob) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

// Utility: convert canvas to Blob with desired type/quality
const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

// Utility: create a canvas with optional scaling
const drawToCanvas = (img, scale = 1) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  canvas.width = w;
  canvas.height = h;
  ctx.drawImage(img, 0, 0, w, h);
  return canvas;
};

// Try to reduce file size under 5MB with minimal perceived quality loss
// Strategy: (1) keep dimensions; export as WebP at high quality. If still too big,
// (2) decrement quality gradually; (3) if still big, scale dimensions down slightly and retry.
export const compressToUnder5MB = async (file) => {
  if (file.size <= MAX_UPLOAD_BYTES) return file; // nothing to do

  try {
    // Prefer WebP for high quality at smaller sizes; fallback to JPEG
    const targetTypes = ["image/webp", "image/jpeg"]; // browsers will pick supported

    // Load image once
    const img = await blobToImage(file);

    // Prevent huge dimension uploads: cap the longest edge to 4000px first pass
    const longest = Math.max(img.width, img.height);
    const initialScale = longest > 4000 ? 4000 / longest : 1;

    for (const type of targetTypes) {
      let scale = initialScale;
      // quality sweep from high to acceptable
      const qualities = [0.95, 0.9, 0.85, 0.8, 0.75, 0.7, 0.65, 0.6];
      for (let attempt = 0; attempt < 6; attempt++) {
        const qList = attempt === 0 ? qualities : [0.85, 0.8, 0.75, 0.7, 0.65, 0.6];
        for (const q of qList) {
          const canvas = drawToCanvas(img, scale);
          const blob = await canvasToBlob(canvas, type, q);
          if (!blob) continue;
          if (blob.size <= MAX_UPLOAD_BYTES) {
            const ext = type === "image/webp" ? "webp" : "jpg";
            const name = file.name.replace(/\.[^.]+$/, `.${ext}`);
            return new File([blob], name, { type });
          }
        }
        // If still too large, scale down 90% and retry
        scale *= 0.9;
        if (scale < 0.4) break; // avoid over-shrinking
      }
    }
  } catch (e) {
    console.warn("Compression failed, uploading original file", e);
  }

  // Fallback to original if compression could not bring size under 5MB
  return file;
};